import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { isNotDefined } from '@sgme/fp';
import { v4 } from 'uuid';

import { getWorkspaceMatchFulfilled, workspaceApi } from '@/api/workspace/index.ts';
import { summonOrderTabsReducer } from '@/store/blotter/workflows/summonOrder/extraReducers.ts';
import { reduceTabsSliceOnSaveSharedUserList } from '@/store/sharedWorkspace/updateSharedUserList.workflow.ts';
import { reduceTabsOnfetchSharedWorkspace } from '@/store/sharedWorkspace/workflows/getSharedTab.workflow.ts';
import { reduceTabStateOnSharedTabRemoved } from '@/store/sharedWorkspace/workflows/onSharedTabRemoved.workflow.ts';
import { reduceOnClickShareTab } from '@/store/sharedWorkspace/workflows/saveSharedTab.workflow.ts';
import { tabsAdapter } from '@/store/tabs/adapter.ts';
import { tabsStateReducers } from '@/store/tabs/reducers.ts';
import type { Tab, TabsState, TabType } from '@/store/tabs/types.ts';
import { generateDefaultTilesTabName } from '@/store/tabs/utils.ts';
import { onCurrentUserDataFetched } from '@/store/tabs/workflows/onCurrentUserDataFetched.workflow.ts';

export const tabsSlice = createSlice({
  name: 'tabs',
  // this initial state will quickly be replaced when we get the current user data at app start
  // the fetching of the current user data fails, the error is fatal anyway, so that this initial state does not matter that much
  initialState: {
    activeTabId: '',
    defaultClientId: 0,
    tabs: tabsAdapter.getInitialState(undefined, []),
  } as TabsState,
  reducers: {
    /**
     * create a new tab with default values
     */
    onClickNewTabButton: (
      state,
      { payload: { id: forcedId, clientId } }: PayloadAction<{ id?: string; clientId: number }>,
    ) => {
      const allTabs = tabsAdapter.getSelectors().selectAll(state.tabs);

      const id = forcedId ?? v4();

      tabsAdapter.addOne(state.tabs, {
        id,
        tabType: 'tiles',
        clientId,
        tabName: generateDefaultTilesTabName(allTabs),
        isShared: false,
      });
      state.activeTabId = id;
    },

    /**
     * delete tab by id
     */
    removeTab: (state, { payload: tabId }: PayloadAction<Tab['id']>) => tabsStateReducers.deleteTab(state, tabId),

    onConfirmRemoveSharedTab: (state, { payload: tabId }: PayloadAction<Tab['id']>) => {
      const tab = state.tabs.entities[tabId];
      if (!tab?.isShared) {
        console.warn(
          `try to call action 'onConfirmRemoveSharedTab' with tabid ${tabId}, but either tab is not found or tab is not shared`,
          { tab },
        );
        return;
      }
      tabsStateReducers.deleteTab(state, tabId);
    },

    /**
     * select tab by id
     */
    changeActiveTab: (state, { payload }: PayloadAction<{ tabId: Tab['id'] }>) => {
      if (state.tabs.ids.includes(payload.tabId)) {
        state.activeTabId = payload.tabId;
      }
    },

    /**
     * set the tab name for given tab by id
     */
    setTabName: (state, { payload }: PayloadAction<{ tabId: Tab['id']; tabName: string }>) => {
      tabsAdapter.updateOne(state.tabs, { id: payload.tabId, changes: { tabName: payload.tabName } });
    },

    /**
     * select a client for a given tab id
     */
    changeClient: (state, { payload }: PayloadAction<{ tabId: Tab['id']; clientId: number }>) => {
      tabsAdapter.updateOne(state.tabs, { id: payload.tabId, changes: { clientId: payload.clientId } });
    },

    /**
     * set the tab type for a given tab id
     */
    setTabType: (state, { payload }: PayloadAction<{ tabId: Tab['id']; type: TabType }>) => {
      tabsAdapter.updateOne(state.tabs, { id: payload.tabId, changes: { tabType: payload.type } });
    },

    /**
     * set of unset the zoomed tile for the active tab.
     * nilpotent if the active tab type is not 'tiles'.
     */
    setZoomedTileIdForActiveTab: (state, { payload }: PayloadAction<{ tileId: string | undefined }>) => {
      const activeTabId = state.tabs.ids.includes(state.activeTabId) ? state.activeTabId : (state.tabs.ids[0] ?? '');

      if (state.tabs.entities[activeTabId]?.tabType !== 'tiles') {
        return;
      }
      tabsAdapter.updateOne(state.tabs, { id: activeTabId, changes: { zoomedTileId: payload.tileId } });
    },
  },
  extraReducers(builder) {
    reduceTabStateOnSharedTabRemoved(builder);
    reduceTabsSliceOnSaveSharedUserList(builder);
    summonOrderTabsReducer(builder);
    reduceOnClickShareTab(builder);
    reduceTabsOnfetchSharedWorkspace(builder);
    onCurrentUserDataFetched(builder);
    /////////////////////////////////////////////////////////////////////////////////////////
    // when unshare successfully, remove shared users from tab                             //
    //   not externalized as we want to keep it private to avoid circular dependency       //
    /////////////////////////////////////////////////////////////////////////////////////////
    builder.addMatcher(workspaceApi.endpoints.stopSharing.matchFulfilled, (state, action) => {
      const tabId = action.meta.arg.originalArgs.tabId;
      tabsAdapter.updateOne(state.tabs, {
        id: tabId,
        changes: { isShared: false, sharedWithUsers: undefined, ownerEmail: undefined },
      });
    });

    builder.addMatcher(getWorkspaceMatchFulfilled, (state, action) => {
      if (!action.payload) {
        return;
      }
      tabsAdapter.removeAll(state.tabs);
      tabsAdapter.addMany(
        state.tabs,
        action.payload.tabs.map((tab) => ({
          ...tab,
          // the clientId is sometimes a string, so we ensure it's a number
          clientId: Number(tab.clientId),
          isShared: false,
        })),
      );
      const { selectedTabId } = action.payload;
      state.activeTabId = selectedTabId;
    });
  },
  selectors: {
    selectTabsState: (state) => state,
    selectActiveTabId: (state) => {
      return state.tabs.ids.includes(state.activeTabId) ? state.activeTabId : (state.tabs.ids[0] ?? '');
    },
    selectActiveTab: (state) => {
      const activeTabId = state.tabs.ids.includes(state.activeTabId) ? state.activeTabId : (state.tabs.ids[0] ?? '');
      return tabsAdapter.getSelectors().selectById(state.tabs, activeTabId);
    },
    selectActiveTabZoomedTileId: (state) => {
      const activeTabId = state.tabs.ids.includes(state.activeTabId) ? state.activeTabId : (state.tabs.ids[0] ?? '');
      const activeTab = tabsAdapter.getSelectors().selectById(state.tabs, activeTabId);
      if (isNotDefined(activeTab) || activeTab.tabType !== 'tiles') {
        return undefined;
      }
      return activeTab.zoomedTileId;
    },
  },
});

export const { selectTabsState, selectActiveTabId, selectActiveTab, selectActiveTabZoomedTileId } = tabsSlice.selectors;

export const {
  onClickNewTabButton,
  removeTab,
  changeActiveTab,
  setTabName,
  changeClient,
  setTabType,
  onConfirmRemoveSharedTab,
  setZoomedTileIdForActiveTab,
} = tabsSlice.actions;
